import React, { useEffect, useContext, useState } from 'react';
import clsx from 'clsx';
import { CHART_DATE_INITIALIZATION_TYPES } from 'src/redux/layout/topListLayoutSchema';
import { CLIENT_IDS, DEFAULT_LAYOUT_ID, DEFAULT_TEMPLATE_ID } from 'src/app/ChartingLibrary/constants';
import LayoutContext from 'src/app/TopListsMosaic/layout/LayoutContext';
import { useSelector, useDispatch } from 'react-redux';
import { selectComponent } from 'src/redux/layout/topListLayoutSelectors';
import { updateComponent } from 'src/redux/layout/topListLayoutActions';
import useToplistLinkedValues from 'src/hooks/useToplistLinkedValues';
import MosaicPanelHeader from 'src/app/TopListsMosaic/layout/MosaicPanelHeader/MosaicPanelHeader';
import MosaicPanel from 'src/app/TopListsMosaic/layout/MosaicPanel';
import MosaicPanelBody from 'src/app/TopListsMosaic/layout/MosaicPanelBody';
import SmallFilterWindow from 'src/app/components/filterContainers/SmallFilterWindow';
import PanelIconButton from 'src/app/components/panels/PanelIconButton';
import { ExpandIcon, MarketOpenIcon, RefreshIcon } from 'src/theme/EdgeIcons';
import AutosavedChart from 'src/app/ChartingLibrary/AutosavedChart';
import { MosaicWindowContext } from 'react-mosaic-component';
import { useDeepCompareEffect } from 'src/hooks/useDeepCompare';
import DateLinkDropdown from 'src/app/TopListsMosaic/TopListChart/DateLinkDropdown';
import {
  alpha,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import {
  addBusinessDays,
  getCurrentTradingDay,
  getMostRecentTradingDay,
  formatMarketTime, parseAssumeMarketTime
} from 'src/utils/datetime/date-fns.tz';
import { DATE_TYPE_LABELS } from 'src/app/slicedForm/FilterForm/definitions/inputEnums';


const useStyles = makeStyles(() => ({
  root: {},
  filterWindowFormControl: {
    padding: '7px 0',
  },
  chartPanel: {
    borderRadius: '0 !important'
  },
  chartPortalContainer: {
    height: '100%',
    display: 'flex',
    flex: 1,
  },
}));


/**
 * There are 3 possible dates the user can switch between.
 * @see CHART_DATE_INITIALIZATION_TYPES
 *
 * @param {keyof CHART_DATE_INITIALIZATION_TYPES} dateInitializationType
 * @param {string} historicalDate
 * @param {string} specificallySelectedDay
 * @return {string|null}
 */
const decideSelectedDate = (dateInitializationType, historicalDate, specificallySelectedDay) => {
  if (dateInitializationType === CHART_DATE_INITIALIZATION_TYPES.TODAY) {
    return null;
  } else if (dateInitializationType === CHART_DATE_INITIALIZATION_TYPES.LINKED_DATE) {
    return historicalDate;
  } else if (dateInitializationType === CHART_DATE_INITIALIZATION_TYPES.SELECT_DATE) {
    return specificallySelectedDay;
  }
};


function TopListChart({ className }) {
  const classes = useStyles();
  const { componentId, layoutId } = useContext(LayoutContext);
  const { mosaicWindowActions } = useContext(MosaicWindowContext);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { linkedData, memoizedUpdateLinkedData } = useToplistLinkedValues();
  /** @type {{string, string}} */
  const { ticker = 'AAPL', historicalDate } = linkedData;

  const {
    selectedTemplateId = DEFAULT_TEMPLATE_ID,
    interval,
    /** @type {CHART_DATE_INITIALIZATION_TYPES} */
    dateInitializationType,
    /** @type {string} */
    specificallySelectedDay = null
  } = useSelector(selectComponent(componentId, layoutId));

  const [selectedDate, setSelectedDate] = useState(() => decideSelectedDate(dateInitializationType, historicalDate, specificallySelectedDay));
  const isViewingLiveData = !selectedDate || selectedDate === formatMarketTime(getMostRecentTradingDay(), 'yyyy-MM-dd');
  const isToday = (!selectedDate || selectedDate === formatMarketTime(getCurrentTradingDay(), 'yyyy-MM-dd'));

  const [remountChart, setRemountChart] = useState(0);
  const mosaicPath = mosaicWindowActions.getPath();


  useDeepCompareEffect(() => {
    setRemountChart(remountChart + 1);
  }, [mosaicPath]);


  useEffect(() => {
    if (dateInitializationType === CHART_DATE_INITIALIZATION_TYPES.LINKED_DATE) {
      setSelectedDate(historicalDate);
    }
  }, [historicalDate]);


  useEffect(() => {
    if (ticker) {
      const resolvedDate = decideSelectedDate(dateInitializationType, historicalDate, specificallySelectedDay);
      setSelectedDate(resolvedDate);
    }
  }, [ticker]);


  const dispatchTicker = (ticker) => {
    if (ticker) {
      dispatch(memoizedUpdateLinkedData({ ticker }));
    }
  };


  const handleReset = () => {
    setSelectedDate(null);
    if (dateInitializationType === CHART_DATE_INITIALIZATION_TYPES.LINKED_DATE) {
      dispatch(memoizedUpdateLinkedData({ historicalDate: null }));
    }
  };


  const handleDayGoButtonClick = () => {
    const resolvedDate = decideSelectedDate(dateInitializationType, historicalDate, specificallySelectedDay);
    setSelectedDate(resolvedDate);
  };


  const handleNextDay = () => {
    if (!selectedDate) return;
    const date = parseAssumeMarketTime(selectedDate, 'yyyy-MM-dd');
    setSelectedDate(formatMarketTime(addBusinessDays(date, 1), 'yyyy-MM-dd'));
  };


  const handleDatePickerAccept = (datestring) => {
    dispatch(updateComponent(componentId, layoutId, {
      specificallySelectedDay: datestring === formatMarketTime(getMostRecentTradingDay(), 'yyyy-MM-dd') ? null : datestring
    }));
  };


  /**
   *
   * @param {Object} event
   * @param {keyof CHART_DATE_INITIALIZATION_TYPES} value
   */
  const handleDateInitializationTypeChange = (event, value) => {
    if (!value || dateInitializationType === value) return;
    dispatch(updateComponent(componentId, layoutId, { dateInitializationType: value }));
  };


  const handleTemplateSelect = (templateId) => {
    dispatch(updateComponent(componentId, layoutId, { selectedTemplateId: templateId }));
  };


  const handleLayoutSelect = (chartLayoutId) => {
    dispatch(updateComponent(componentId, layoutId, { selectedLayoutId: chartLayoutId }));
  };


  const handleIntervalChange = (interval) => {
    dispatch(updateComponent(componentId, layoutId, { interval }));
  };


  return (
    <MosaicPanel className={clsx(className, classes.root)}>
      <MosaicPanelHeader
        tickerSearchValue={ticker}
        onTickerSearchSubmit={dispatchTicker}
        title={' '}
        titleSuppliment={isToday ? 'today' : selectedDate}
        titleSupplimentColor={alpha(isToday ? theme.palette.text.positive : theme.palette.text.secondary, .7)}
        loading={false}
      >
        {selectedDate && (
          <>
            <PanelIconButton
              Icon={RefreshIcon}
              onClick={handleReset}
              text="Reset"
              shouldHideIconText={true}
            />
            <PanelIconButton
              Icon={ExpandIcon}
              onClick={handleNextDay}
              text="Next Day"
              shouldHideIconText={true}
            />
          </>
        )}
        <SmallFilterWindow
          iconText="Date"
          Icon={MarketOpenIcon}
          iconColor={isViewingLiveData ? theme.palette.text.primary : theme.palette.primary.main}
          shouldHideIconText={true}
          // popoverTitle="Change Date"
          popoverMinWidth={392}
        >
          <DateLinkDropdown
            toggleValue={dateInitializationType}
            selectedDay={specificallySelectedDay}
            onToggleChange={handleDateInitializationTypeChange}
            onDatePickerAccept={handleDatePickerAccept}
            onGoButtonClick={handleDayGoButtonClick}
          />
        </SmallFilterWindow>
      </MosaicPanelHeader>
      <MosaicPanelBody
        loading={false}
        className={classes.chartPortalContainer}
      >
        <AutosavedChart
          key={remountChart <= 1 ? 0 : remountChart}
          className={classes.chartPanel}
          tickerId={ticker}
          componentId={componentId}
          selectedLayoutId={DEFAULT_LAYOUT_ID}
          selectedTemplateId={selectedTemplateId}
          clientId={CLIENT_IDS.GLOBAL_V1}
          fromDate={selectedDate || null}
          interval={interval}
          isRealtime
          onTemplateSelect={handleTemplateSelect}
          onLayoutSelect={handleLayoutSelect}
          onIntervalChange={handleIntervalChange}
        />
      </MosaicPanelBody>
    </MosaicPanel>
  );
}

export default TopListChart;

